import type { AdSettings } from '../api/adSettingsApi';

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** The budget as a figure, or that none is on file. */
export function budgetFigure(s: Pick<AdSettings, 'monthlyAdBudget'>): string {
  return s.monthlyAdBudget === null ? 'None on file' : `${money(s.monthlyAdBudget)} per month`;
}

/** Where the budget came from: the campaigns the practice approved, or an agency override, and when. */
export function budgetSource(
  s: Pick<AdSettings, 'monthlyAdBudget' | 'budgetSetBy' | 'budgetSetAt'>,
): string {
  if (s.monthlyAdBudget === null && s.budgetSetBy === null)
    return 'Set when you approve campaign budgets in Ad Campaigns.';
  const when = s.budgetSetAt ? `, ${day(s.budgetSetAt)}` : '';
  if (s.budgetSetBy === 'agency_override') return `Overridden by your agency${when}.`;
  if (s.budgetSetBy === 'approved_change')
    return `Raised when you approved Jenny's budget change in Google Ads${when}.`;
  if (s.budgetSetBy === 'campaigns')
    return `The total of the campaign budgets approved in Ad Campaigns${when}.`;
  return 'The total of the campaign budgets approved in Ad Campaigns.';
}

/** Each location's budget and share, when the practice has more than one: the split recommendations stay inside. */
export function locationSplit(s: Pick<AdSettings, 'locationBudgets'>): string | null {
  if (s.locationBudgets.length < 2) return null;
  return s.locationBudgets
    .map((l) =>
      l.monthlyAdBudget === null
        ? `${l.name}: none on file`
        : `${l.name}: ${money(l.monthlyAdBudget)}${l.sharePercent !== null ? ` (${l.sharePercent}%)` : ''}`,
    )
    .join(' · ');
}
